import { forwardRef, ReactNode } from "react";
import { RouterLink } from "src/router";

import {
  Box,
  BoxProps,
  Column,
  LinkProps,
  Row,
  SectionHeading,
  Text,
} from "@hightouch/htui";
import { Markdown } from "src/ui/markdown";
import { CARD_PB_WITH_FOOTER, DEFAULT_CARD_PADDING } from "./constants";

export type CardProps = Omit<BoxProps, "p"> & {
  /**
   * Determines the bottom padding of the card whether a footer is present or not.
   * To render a footer within a card, use `CardFooter` as the last child of the card.
   */
  hasFooter?: boolean;
  /**
   * If an href is provided, the card will be rendered as a link.
   */
  href?: LinkProps["href"];
  heading?: ReactNode;
  subheading?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  isDisabled?: boolean;
  isActive?: boolean;
  rightContent?: ReactNode;
  p?: number;
  px?: number;
  py?: number;
};

/**
 * Shared card component.
 *
 * To include a footer, use the `CardFooter` component as the last child of the card,
 * and add `hasFooter` to the `Card` component for optimal spacing.
 */
export const Card = forwardRef<HTMLDivElement | HTMLAnchorElement, CardProps>(
  (
    {
      hasFooter = false,
      p = DEFAULT_CARD_PADDING,
      px,
      py,
      children,
      href,
      heading,
      subheading,
      description,
      rightContent,
      isDisabled: _isDisabled = false,
      isActive,
      onClick,
      ...props
    },
    ref,
  ) => {
    const isClickable = Boolean(onClick || href);
    const isDisabled = _isDisabled && isClickable;

    return (
      <Column
        ref={ref}
        as={href ? RouterLink : isClickable ? "button" : "div"}
        to={href}
        w={isClickable && !href ? "100%" : undefined}
        cursor={isDisabled ? "not-allowed" : isClickable ? "pointer" : "auto"}
        bg={isActive ? "primary.background" : "white"}
        borderWidth="hairline"
        borderStyle="solid"
        borderColor={isActive ? "primary.border" : "base.border"}
        borderRadius="xl"
        boxShadow="buttonShadowLight"
        outline="none !important"
        opacity={isDisabled ? 0.6 : 1}
        textAlign={isClickable ? "left" : undefined}
        px={px ?? p}
        py={py ?? p}
        pb={hasFooter ? CARD_PB_WITH_FOOTER : (py ?? p)}
        _hover={{
          borderColor: isClickable ? "gray.400" : undefined,
        }}
        _focusVisible={{
          boxShadow: isClickable && !isDisabled ? "outline" : undefined,
        }}
        // TODO(HTUI-393): "common" includes box-shadow, so the resting and
        // focus-ring shadows above animate on every Card in the app
        transitionProperty="common"
        transitionDuration="fast"
        transitionTimingFunction="easeOutQuad"
        onClick={onClick}
        {...props}
      >
        {(heading || rightContent) && (
          <Row
            justify="space-between"
            // A single-line heading is shorter than the action buttons beside it,
            // so center them. With stacked text below, top alignment is correct.
            align={subheading || description ? undefined : "center"}
            gap={4}
            mb={description ? 6 : 0}
          >
            <Box minWidth={0}>
              {heading && (
                <Box as={SectionHeading}>
                  {typeof heading === "string" ? (
                    <Markdown disableParagraphs>{heading}</Markdown>
                  ) : (
                    heading
                  )}
                </Box>
              )}
              {subheading && (
                <Box mt={1}>
                  {typeof subheading === "string" ? (
                    <Markdown>{subheading}</Markdown>
                  ) : (
                    subheading
                  )}
                </Box>
              )}
              {description && (
                <Box mt={1}>
                  {typeof description === "string" ? (
                    <Text color="text.secondary">{description}</Text>
                  ) : (
                    description
                  )}
                </Box>
              )}
            </Box>
            {rightContent && <Box flexShrink={0}>{rightContent}</Box>}
          </Row>
        )}
        {children}
      </Column>
    );
  },
);

Card.displayName = "Card";
